import React, { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader,
  MessageCircle,
  BarChart3,
  Database,
  Clock,
  Eye,
  Zap,
} from "lucide-react";
import { authFetch, apiUrl } from "../../utils/api";
import "./DashboardWizard.css";

const DashboardWizard = ({ onComplete, onCancel, userId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({
    name: "",
    goal: "",
    kpis: [],
    dataSources: [],
    updateFrequency: "manual",
    visualizations: [],
    tabs: [],
  });
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState("");
  const [kpiInput, setKpiInput] = useState("");
  const [tabInput, setTabInput] = useState("");
  // The user's ACTUAL Fort Knox connections — any connected integration can
  // feed a dashboard live through the data proxy, so offer them all, first.
  const [connectedSources, setConnectedSources] = useState([]);
  // Who builds this and on whose usage — shown as step 0 (Brad 2026-09-06).
  const [buildInfo, setBuildInfo] = useState(null);
  const [genError, setGenError] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(apiUrl("/api/custom-dashboards/build-info"));
        setBuildInfo(r.ok ? await r.json() : { connected: false, builder: "none", caveats: [] });
      } catch (_) { setBuildInfo({ connected: false, builder: "none", caveats: [] }); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // 1) Fort Knox integrations
        const res = await authFetch(apiUrl("/api/credentials"));
        const data = await res.json();
        const fromCreds = (data.integrations || [])
          .filter((i) => i.connected)
          .map((i) => ({
            value: String(i.id).toLowerCase(),
            label: `${i.name} — connected`,
            emoji: "✅",
            connected: true,
          }));
        // 2) Custom MCPs (owner or team-shared) — Brad 2026-08-25:
        // wizard must show the tools they ACTUALLY have.
        let fromMcps = [];
        try {
          const m = await authFetch(apiUrl("/api/teams/shared-folders")); // pings deviceOrClerkAuth to know we're authed
          if (m.ok) {
            const mcpRes = await authFetch(apiUrl("/api/mcp-user/list-mine"));
            if (mcpRes.ok) {
              const md = await mcpRes.json();
              fromMcps = (md.tools || []).map((t) => ({
                value: String(t.name).toLowerCase(),
                label: `${t.display_name || t.name} — your custom tool (${t.tool_count} actions)`,
                emoji: "🔧",
                connected: true,
                customMcp: true,
              }));
            }
          }
        } catch (_) { /* MCP enumeration is best-effort */ }
        setConnectedSources([...fromCreds, ...fromMcps]);
      } catch (_) {
        /* offline/unauth — static list still works (sample-data mode) */
      }
    })();
  }, []);

  const POPULAR_SOURCES = [
    { value: "stripe", label: "Stripe", emoji: "💳" },
    { value: "gohighlevel", label: "GoHighLevel", emoji: "🚀" },
    { value: "hubspot", label: "HubSpot", emoji: "🧡" },
    { value: "salesforce", label: "Salesforce", emoji: "☁️" },
    { value: "google_analytics", label: "Google Analytics", emoji: "📊" },
    { value: "facebook_ads", label: "Facebook Ads", emoji: "📘" },
    { value: "shopify", label: "Shopify", emoji: "🛍️" },
    { value: "quickbooks", label: "QuickBooks", emoji: "💰" },
    { value: "mailchimp", label: "Mailchimp", emoji: "📧" },
    { value: "zapier", label: "Zapier", emoji: "⚡" },
    { value: "airtable", label: "Airtable", emoji: "📋" },
    { value: "custom_api", label: "Custom API", emoji: "🔧" },
    { value: "other", label: "Other", emoji: "📦" },
  ];
  const norm = (v) => String(v).toLowerCase().replace(/[^a-z0-9]/g, "");
  const sourceOptions = [
    ...connectedSources,
    ...POPULAR_SOURCES.filter(
      (p) => !connectedSources.some((c) => norm(c.value) === norm(p.value)),
    ),
  ];

  const STEPS = [
    {
      id: "intro",
      title: "Before Fable builds",
      subtitle: "What happens next, whose Claude does the work, and what it costs — no surprises.",
      icon: Sparkles,
      type: "intro",
    },
    {
      id: "name",
      title: "Name this dashboard",
      subtitle:
        "You might have 8 launches running — give this one a name you'll recognize (Melissa's ask).",
      icon: LayoutDashboard,
      type: "text",
      placeholder: "e.g., Sept Launch Signups, Q3 Pipeline Pulse, Jamie's Projections…",
    },
    {
      id: "goal",
      title: "What's your main goal?",
      subtitle: "Tell me what you want to track or monitor in your business",
      icon: MessageCircle,
      type: "text",
      placeholder:
        "e.g., Track sales performance, Monitor marketing ROI, Manage customer pipeline...",
    },
    {
      id: "kpis",
      title: "What are your key metrics?",
      subtitle: "Add 3-5 KPIs (Key Performance Indicators) you want to see",
      icon: BarChart3,
      type: "list",
      placeholder:
        "e.g., Monthly Revenue, Conversion Rate, Active Customers...",
    },
    {
      id: "dataSources",
      title: "Where does this data live?",
      subtitle:
        "Your connected integrations are marked ✅ — those chart LIVE data. Anything else generates with sample data until you connect it.",
      icon: Database,
      type: "multi-select",
      options: sourceOptions,
    },
    {
      id: "visualizations",
      title: "How should we display your data?",
      subtitle: "Pick the visualizations that make sense for your metrics",
      icon: Eye,
      type: "multi-select",
      options: [
        { value: "line_chart", label: "Line Charts", emoji: "📈" },
        { value: "bar_chart", label: "Bar Charts", emoji: "📊" },
        { value: "pie_chart", label: "Pie Charts", emoji: "🥧" },
        { value: "stats", label: "Big Numbers/Stats", emoji: "🔢" },
        { value: "tables", label: "Data Tables", emoji: "📋" },
        { value: "progress", label: "Progress Bars", emoji: "⏳" },
        { value: "trends", label: "Trend Indicators", emoji: "⤴️" },
        { value: "gauges", label: "Gauge Charts", emoji: "🎯" },
      ],
    },
    {
      id: "updateFrequency",
      title: "How often should this update?",
      subtitle: "Choose how frequently you want fresh data",
      icon: Clock,
      type: "select",
      options: [
        { value: "realtime", label: "Real-time (on open)", emoji: "⚡" },
        { value: "hourly", label: "Every Hour", emoji: "⏰" },
        { value: "6hours", label: "Every 6 Hours", emoji: "🕐" },
        { value: "daily", label: "Daily", emoji: "📅" },
        { value: "weekly", label: "Weekly", emoji: "📆" },
        { value: "manual", label: "Manual Only", emoji: "🔄" },
      ],
    },
    {
      id: "tabs",
      title: "Want multiple views?",
      subtitle:
        "Create tabs to organize different aspects of your dashboard (optional)",
      icon: Zap,
      type: "list",
      placeholder: "e.g., Overview, Sales, Marketing, Operations...",
      optional: true,
    },
  ];

  const handleNext = () => {
    const step = STEPS[currentStep];

    // Validation
    if (step.type === "intro" && buildInfo && !buildInfo.connected) {
      setGenError("Connect Claude first (Settings → Integrations → Claude). Fable builds dashboards on your own Claude plan.");
      return;
    }
    if (!step.optional) {
      if (step.type === "text" && !responses[step.id]) {
        alert("Please fill in this field");
        return;
      }
      if (step.type === "list" && responses[step.id].length === 0) {
        alert("Please add at least one item");
        return;
      }
      if (step.type === "multi-select" && responses[step.id].length === 0) {
        alert("Please select at least one option");
        return;
      }
    }

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleGenerate();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleTextChange = (value) => {
    const step = STEPS[currentStep];
    setResponses({ ...responses, [step.id]: value });
  };

  const handleAddToList = () => {
    const step = STEPS[currentStep];
    const input = step.id === "kpis" ? kpiInput : tabInput;

    if (!input.trim()) return;

    setResponses({
      ...responses,
      [step.id]: [...responses[step.id], input.trim()],
    });

    if (step.id === "kpis") {
      setKpiInput("");
    } else {
      setTabInput("");
    }
  };

  const handleRemoveFromList = (index) => {
    const step = STEPS[currentStep];
    const newList = responses[step.id].filter((_, i) => i !== index);
    setResponses({ ...responses, [step.id]: newList });
  };

  const handleToggleOption = (value) => {
    const step = STEPS[currentStep];
    const current = responses[step.id] || [];

    if (current.includes(value)) {
      setResponses({
        ...responses,
        [step.id]: current.filter((v) => v !== value),
      });
    } else {
      setResponses({
        ...responses,
        [step.id]: [...current, value],
      });
    }
  };

  const handleSelectOption = (value) => {
    const step = STEPS[currentStep];
    setResponses({ ...responses, [step.id]: value });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress(0);
    setGenerationStep("Analyzing your requirements");

    try {
      // Step 1: Analyzing (0-15%)
      setGenerationProgress(5);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setGenerationProgress(10);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setGenerationProgress(15);

      // Step 2: Generating code (15-50%) - This is the slow part
      setGenerationStep("Fable is writing the dashboard");
      setGenerationProgress(20);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setGenerationProgress(25);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setGenerationProgress(30);

      // Call the dashboard generation API (this takes 10-20 seconds)
      const response = await authFetch(
        apiUrl("/api/custom-dashboards/generate"),
        {
          method: "POST",
          body: JSON.stringify({
            requirements: responses,
          }),
        },
      );

      // API call complete - jump to 60%
      setGenerationProgress(60);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const e = new Error(errorData.message || errorData.error || "Failed to generate dashboard");
        e.code = errorData.code;
        throw e;
      }

      // Step 3: Creating visualizations (60-100%)
      setGenerationStep("Creating visualizations");
      setGenerationProgress(70);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const dashboardConfig = await response.json();

      // Ensure we have valid tabs with content
      if (!dashboardConfig.tabs || dashboardConfig.tabs.length === 0) {
        throw new Error("Dashboard generation returned no content");
      }

      setGenerationProgress(85);
      await new Promise((resolve) => setTimeout(resolve, 300));
      setGenerationProgress(100);
      setGenerationStep("Complete!");

      await new Promise((resolve) => setTimeout(resolve, 500));
      onComplete(dashboardConfig);
    } catch (error) {
      console.error("Error generating dashboard:", error);
      setGenError(error.message || "Failed to generate dashboard. Please try again.");
      setGenerating(false);
      setGenerationProgress(0);
      setGenerationStep("");
    }
  };

  const step = STEPS[currentStep];
  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  if (generating) {
    return (
      <div className="wizard-generating">
        <div className="generation-icon">
          <Sparkles size={64} className="pulse" />
        </div>
        <h2>Fable is designing your dashboard…</h2>
        <p>Writing the layout, wiring your live data, checking every state. Usually 30–90 seconds.</p>

        {/* Progress Bar */}
        <div className="generation-progress-bar">
          <div
            className="generation-progress-fill"
            style={{ width: `${generationProgress}%` }}
          />
        </div>
        <div className="generation-progress-text">
          {generationProgress}% - {generationStep}
        </div>

        <div className="generating-steps">
          <div
            className={`gen-step ${generationProgress >= 10 ? "complete" : generationProgress > 0 ? "active" : ""}`}
          >
            {generationProgress >= 30 ? (
              <CheckCircle size={20} />
            ) : (
              <Loader size={20} className="spin" />
            )}
            Analyzing your requirements
          </div>
          <div
            className={`gen-step ${generationProgress >= 70 ? "complete" : generationProgress >= 30 ? "active" : ""}`}
          >
            {generationProgress >= 70 ? (
              <CheckCircle size={20} />
            ) : generationProgress >= 30 ? (
              <Loader size={20} className="spin" />
            ) : (
              <Sparkles size={20} />
            )}
            Generating dashboard code
          </div>
          <div
            className={`gen-step ${generationProgress === 100 ? "complete" : generationProgress >= 70 ? "active" : ""}`}
          >
            {generationProgress === 100 ? (
              <CheckCircle size={20} />
            ) : generationProgress >= 70 ? (
              <Loader size={20} className="spin" />
            ) : (
              <Sparkles size={20} />
            )}
            Creating visualizations
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wizard">
      {/* Header */}
      <div className="wizard-header">
        <div className="wizard-title">
          <Sparkles size={28} />
          <div>
            <h2>Dashboard Builder</h2>
            <p>Let's create your perfect dashboard</p>
          </div>
        </div>
        <div className="wizard-progress">
          <span className="progress-text">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        <div className="step-icon">
          <StepIcon size={48} />
        </div>

        <h3 className="step-title">{step.title}</h3>
        <p className="step-subtitle">{step.subtitle}</p>

        <div className="step-input">
          {step.type === "intro" && (
            <div className="wizard-intro">
              <ul className="wizard-caveats">
                {(buildInfo?.caveats || ["Loading…"]).map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
              <div className={`wizard-claude-status ${buildInfo?.connected ? "ok" : "warn"}`}>
                {buildInfo == null
                  ? "Checking your Claude connection…"
                  : buildInfo.connected
                      ? `Builder: your Claude ${buildInfo.tokenType === "subscription" ? "subscription" : "API key"} · ready`
                      : "Claude isn't connected — connect it in Settings → Integrations, then come back. The build will not run without it."}
              </div>
              {buildInfo?.model && (
                <p className="wizard-model-note">Model: {buildInfo.model} · Optimus Kit {buildInfo.kitVersion}</p>
              )}
            </div>
          )}
          {step.type === "text" && (
            <textarea
              id={`wizard-${step.id}`}
              name={step.id}
              className="wizard-textarea"
              placeholder={step.placeholder}
              value={responses[step.id] || ""}
              onChange={(e) => handleTextChange(e.target.value)}
              autoFocus
              rows={4}
            />
          )}

          {step.type === "list" && (
            <div className="list-input">
              <div className="input-with-button">
                <input
                  id={`wizard-${step.id}-input`}
                  name={`${step.id}-input`}
                  type="text"
                  className="wizard-input"
                  placeholder={step.placeholder}
                  value={step.id === "kpis" ? kpiInput : tabInput}
                  onChange={(e) =>
                    step.id === "kpis"
                      ? setKpiInput(e.target.value)
                      : setTabInput(e.target.value)
                  }
                  onKeyPress={(e) => e.key === "Enter" && handleAddToList()}
                  autoFocus
                />
                <button
                  className="add-btn"
                  onClick={handleAddToList}
                  disabled={
                    step.id === "kpis" ? !kpiInput.trim() : !tabInput.trim()
                  }
                >
                  Add
                </button>
              </div>

              <div className="list-items">
                {responses[step.id].map((item, index) => (
                  <div key={index} className="list-item">
                    <span>{item}</span>
                    <button onClick={() => handleRemoveFromList(index)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {step.optional && responses[step.id].length === 0 && (
                <p className="optional-hint">
                  Optional: Skip this if you want a single-page dashboard
                </p>
              )}
            </div>
          )}

          {step.type === "multi-select" && step.id === "dataSources" && connectedSources.length === 0 && (
            <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 10, padding: "14px 16px", marginBottom: 14, fontSize: 13.5, color: "var(--text-primary)", lineHeight: 1.55 }}>
              <strong style={{ color: "#fbbf24" }}>You haven't connected any tools yet.</strong>{" "}
              A dashboard needs a data source — <a href="#" style={{ color: "#00d4ff", textDecoration: "underline" }} onClick={(e) => { e.preventDefault(); onClose?.(); window.dispatchEvent(new CustomEvent("open-settings", { detail: { tab: "integrations" } })); }}>connect one in Settings → 🔌 Integrations</a> and come back. You can still pick from the popular list below to preview with sample data, but the real value shows up once your keys are in.
            </div>
          )}

          {step.type === "multi-select" && (
            <div className="multi-select-grid">
              {step.options.map((option) => (
                <button
                  key={option.value}
                  className={`option-card ${responses[step.id]?.includes(option.value) ? "selected" : ""}`}
                  onClick={() => handleToggleOption(option.value)}
                >
                  <span className="option-emoji">{option.emoji}</span>
                  <span className="option-label">{option.label}</span>
                  {responses[step.id]?.includes(option.value) && (
                    <CheckCircle size={20} className="check-icon" />
                  )}
                </button>
              ))}
            </div>
          )}

          {step.type === "select" && (
            <div className="select-grid">
              {step.options.map((option) => (
                <button
                  key={option.value}
                  className={`select-card ${responses[step.id] === option.value ? "selected" : ""}`}
                  onClick={() => handleSelectOption(option.value)}
                >
                  <span className="option-emoji">{option.emoji}</span>
                  <span className="option-label">{option.label}</span>
                  {responses[step.id] === option.value && (
                    <CheckCircle size={20} className="check-icon" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {genError && (
        <div className="wizard-gen-error" role="alert">
          {genError}
          <button type="button" className="wizard-gen-error-close" onClick={() => setGenError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Footer Actions */}
      <div className="wizard-footer">
        <button className="wizard-btn secondary" onClick={onCancel}>
          Cancel
        </button>

        <div className="wizard-nav">
          {currentStep > 0 && (
            <button className="wizard-btn secondary" onClick={handleBack}>
              <ArrowLeft size={18} />
              Back
            </button>
          )}

          <button className="wizard-btn primary" onClick={handleNext} disabled={step.type === "intro" && (buildInfo == null || !buildInfo.connected)}>
            {currentStep === STEPS.length - 1 ? (
              <>
                <Sparkles size={18} />
                Generate Dashboard
              </>
            ) : (
              <>
                Next
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardWizard;
